import * as React from 'react';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Empty from 'antd/lib/empty';
import Button from 'antd/lib/button';
import { compact } from 'lodash';
import { StepGraphStep } from '@/types/workflow';
import { AppPlatform, AppStoreType, CommonNotification } from '../../types';
import { InlineStepConfigDescription } from '../common/InlineStepConfigDescription';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';

interface AddStepOverrideModalProps {
    open: boolean;
    notification: CommonNotification | null;
    stepGraphStepsMap: Record<string, StepGraphStep> | null;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    onClose: () => void;
    onSelect: (step: StepGraphStep) => void;
}

const AddStepOverrideModal: React.FC<AddStepOverrideModalProps> = ({
    open,
    notification,
    stepGraphStepsMap,
    platform,
    appStoreType,
    onClose,
    onSelect,
}) => {
    const candidates = React.useMemo<StepGraphStep[]>(() => {
        if (!notification || !stepGraphStepsMap) return [];
        const archetypes = new Set(notification.applicableStepArchetypes ?? []);
        const overriddenIds = new Set((notification.stepConfigOverrides ?? []).map((o) => o.stepConfigId));
        return Object.values(stepGraphStepsMap).filter(
            (s) => archetypes.has(s.archetype) && !overriddenIds.has(s.stepConfigId),
        );
    }, [notification, stepGraphStepsMap]);

    const [selectedStepConfigId, setSelectedStepConfigId] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (open) setSelectedStepConfigId(null);
    }, [open, notification?.id]);

    const userCanContinue = useUserCanPerformAction(RunwayUserAction.UAUpdateAppSettings);

    const handleConfirm = () => {
        const step = candidates.find((s) => s.stepConfigId === selectedStepConfigId);
        if (step) onSelect(step);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={notification ? `Add override for '${notification.displayText}' notification` : 'Add override'}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <PermissionedActionButton
                    key="ok"
                    actionType={RunwayUserAction.UAUpdateAppSettings}
                    type="primary"
                    disabled={!selectedStepConfigId || !userCanContinue}
                    onClick={handleConfirm}
                >
                    Continue
                </PermissionedActionButton>,
            ]}
            destroyOnClose
        >
            {candidates.length === 0 ?
                <Empty description="All applicable steps already have an override." />
            :   <Select
                    value={selectedStepConfigId}
                    placeholder="Select a step"
                    style={{ width: '100%' }}
                    onChange={(v) => setSelectedStepConfigId(v)}
                    options={compact(
                        candidates.map((step) => ({
                            value: step.stepConfigId,
                            label: (
                                <InlineStepConfigDescription
                                    stepGraphStep={step}
                                    platform={platform}
                                    appStoreType={appStoreType}
                                    isWorkflowsCIStepEnabled
                                />
                            ),
                        })),
                    )}
                />
            }
        </Modal>
    );
};

export default AddStepOverrideModal;
